<template>
  <div class="delete bg-wild-sand pt-6 sm:px-6 text-abbey">
    <BasePanel class="bg-white">
      <BasePanelHeader class="relative">
        <span class="font-semibold text-abbey text-sm"
          >Delete Action Items</span
        >
      </BasePanelHeader>
      <div class="px-4 py-5">
        <Loading
          :class="[
            'flex flex-col',
            {
              'justify-center items-center p-8': isLoading
            }
          ]"
          :isLoading="isLoading"
        >
          <template #success>
            <div class="w-96 relative flex">
              <div class="mr-12 text-xs w-1/2 block">
                <span>Step 1: Franchisor</span>
                <hr />
                <div class="mt-4">
                  <input
                    class="border rounded py-2 px-1 text-gray-500 w-full"
                    type="text"
                    placeholder="Franchisor ID"
                    v-model="selectedZorId"
                  />
                </div>

                <div class="mb-8 mt-4">
                  <div class="base-dropdown-franchisors mt-4">
                    <BaseDropdown class="text-gray-500">
                      <template #button>
                        <div
                          class="hover:bg-wild-sand py-2 px-1 border rounded"
                        >
                          <div class="flex justify-between">
                            <span class="mr-8">{{ franchisorName }}</span>
                            <BaseIcon name="chevron-down" width="12" />
                          </div>
                        </div>
                      </template>

                      <template #menu>
                        <BaseButton
                          class="w-full block"
                          :key="item.id"
                          @click="onZorMenuItemClick(item)"
                          type="button"
                          v-for="item in accessibleFranchisors"
                        >
                          <div
                            class="hover:bg-wild-sand text-sm py-2 px-4 text-left"
                          >
                            {{ item.name }}
                          </div>
                        </BaseButton>
                      </template>
                    </BaseDropdown>
                  </div>
                </div>
                <span>Step 2: Dates</span>
                <hr />
                <div class="base-dropdown-date mr-12 mt-4 w-full block">
                  <div class="w-full block">
                    <BaseDropdown class="text-gray-500 w-full block">
                      <template #button>
                        <div
                          class="hover:bg-wild-sand py-2 px-1 border rounded w-full block"
                        >
                          <div class="flex justify-between">
                            <span class="mr-8">{{ dateItemTitle }}</span>
                            <BaseIcon name="chevron-down" width="12" />
                          </div>
                        </div>
                      </template>

                      <template #menu>
                        <BaseButton
                          class="w-full block"
                          :key="item"
                          @click="onDateMenuItemClick(item)"
                          type="button"
                          v-for="item in items"
                        >
                          <div
                            class="hover:bg-wild-sand text-sm py-2 px-4 leading-none text-left"
                          >
                            {{ item }}
                          </div>
                        </BaseButton>
                      </template>
                    </BaseDropdown>
                  </div>
                </div>
              </div>

              <div
                class="flex flex-col"
                style="min-width: 300px"
                v-if="hasSelectedDateItem"
              >
                <span class="mb-4 mx-auto text-sm font-semibold">{{
                  dateItemTitle
                }}</span>
                <Calendar
                  :month="month"
                  :year="year"
                  :dates="dates"
                  :disableAfter="initialDate"
                  @day-click="onDayClick"
                  v-if="showCalendar"
                />
                <CalendarRangePicker
                  :dates="dates"
                  :initialDate="initialDate"
                  @pick="updateDates"
                  v-else
                />
                <div class="flex flex-col pt-3" v-if="dates.length">
                  <div>
                    <BaseButton
                      class="px-1 py-1 border rounded mb-3 text-xs"
                      @click="clearDates"
                      type="button"
                      >Clear Dates</BaseButton
                    >
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col mt-8">
              <div>
                <div>
                  <BaseButton
                    v-if="dates.length > 0"
                    @click="preSubmitCheck"
                    type="button"
                    :class="[
                      'px-4 py-2 border rounded bg-wild-sand text-abbey font-semibold',
                      { 'opacity-50': showing }
                    ]"
                  >
                    SUBMIT
                  </BaseButton>
                  <BaseTransition type="fade">
                    <BaseModal @close="showing = false" v-if="showing">
                      <div
                        style="width: 500px"
                        class="h-56 p-4 flex flex-col relative"
                      >
                        <h4 class="text-center font-semibold mb-3">
                          Confirm Selection
                        </h4>
                        <span> {{ selectedDateItem }}</span>
                        <span v-if="formattedDates.length === 1">{{
                          formattedDates[0]
                        }}</span>
                        <div
                          class="flex flex-col"
                          v-if="formattedDates.length === 2"
                        >
                          <span>{{ formattedDates[0] }}</span>
                          <span>and</span>
                          <span>{{ formattedDates[1] }}</span>
                        </div>
                        <BaseButton
                          @click="onSubmit"
                          type="button"
                          style="bottom: 10px"
                          class="absolute px-4 py-2 border rounded bg-wild-sand text-grey-200"
                        >
                          Confirm
                        </BaseButton>
                      </div>
                    </BaseModal>
                  </BaseTransition>
                </div>
              </div>
            </div>
          </template>
        </Loading>
      </div>
    </BasePanel>
  </div>
</template>

<script>
import { Calendar, CalendarRangePicker, Loading } from '@listen360/alexandria'
import isFuture from 'date-fns/isFuture'
import isEmpty from 'lodash/isEmpty'

import { mapGetters, mapActions } from 'vuex'

const DATE_OPTIONS = {
  DELETE_ALL_ITEMS_ON: 'Delete All Items ON',
  DELETE_ALL_ITEMS_BEFORE: 'Delete All Items BEFORE',
  DELETE_ALL_ITEMS_AFTER: 'Delete All Items AFTER',
  DELETE_ALL_ITEMS_BETWEEN: 'Delete All Items BETWEEN'
}

export default {
  name: 'Delete',
  components: {
    Calendar,
    CalendarRangePicker,
    Loading
  },
  data() {
    return {
      initialDate: new Date(),
      dates: [],
      items: [
        DATE_OPTIONS.DELETE_ALL_ITEMS_ON,
        DATE_OPTIONS.DELETE_ALL_ITEMS_BEFORE,
        DATE_OPTIONS.DELETE_ALL_ITEMS_AFTER,
        DATE_OPTIONS.DELETE_ALL_ITEMS_BETWEEN
      ],
      franchisorId: '',
      submitted: false,
      selectedDateItem: '',
      selectedZorItem: {},
      selectedZorId: '',
      showing: false,
      params: {}
    }
  },
  computed: {
    ...mapGetters({
      accessibleFranchisors: 'user/accessibleFranchisors'
    }),
    showCalendar() {
      return this.dateItemTitle !== DATE_OPTIONS.DELETE_ALL_ITEMS_BETWEEN
    },
    isLoading() {
      return this.accessibleFranchisors.length === 0 || this.submitted
    },
    // items() {
    //   let arr = []
    //   for (const [key, value] of Object.entries(this.dateOptions)) {
    //     arr.push({ key, value })
    //   }
    //   return arr
    // },
    month() {
      return this.initialDate.getMonth()
    },
    year() {
      return this.initialDate.getFullYear()
    },
    formattedDates() {
      return this.dates.map(date => date.toDateString())
    },
    isoDates() {
      return this.dates.map(date => date.toISOString())
    },
    disable() {
      return (
        !this.accessibleFranchisors.find(
          item => item.id === this.franchisorId
        ) || !this.dates[0]
      )
    },
    hasSelectedFranchisor() {
      return !isEmpty(this.selectedZorItem)
    },
    selectedFranchisorDetails() {
      return this.hasSelectedFranchisor ? this.selectedZorItem : {}
    },
    franchisorName() {
      return this.hasSelectedFranchisor
        ? this.selectedFranchisorDetails.name
        : 'Select Franchisor'
    },
    selectedFranchisorId() {
      return this.hasSelectedFranchisor ? this.selectedFranchisorDetails.id : ''
    },
    hasSelectedDateItem() {
      return Boolean(this.selectedDateItem)
    },
    dateItemTitle() {
      return this.hasSelectedDateItem
        ? this.selectedDateItem
        : 'Select Date Option'
    },
    payloadParams() {
      let obj = {}
      obj.franchisorId = this.selectedFranchisorId
      if (this.selectedDateItem === DATE_OPTIONS.DELETE_ALL_ITEMS_ON) {
        obj.start_date = this.isoDates[0]
        obj.end_date = this.isoDates[0]
      } else if (
        this.selectedDateItem === DATE_OPTIONS.DELETE_ALL_ITEMS_BEFORE
      ) {
        obj.start_date = null
        obj.end_date = this.isoDates[0]
      } else if (
        this.selectedDateItem === DATE_OPTIONS.DELETE_ALL_ITEMS_AFTER
      ) {
        obj.start_date = this.isoDates[0]
        obj.end_date = null
      } else if (
        this.selectedDateItem === DATE_OPTIONS.DELETE_ALL_ITEMS_BETWEEN
      ) {
        obj.start_date = this.isoDates[0]
        obj.end_date = this.isoDates[1]
      }
      return obj
    }
  },
  methods: {
    ...mapActions({
      sendDeleteItems: 'deleteItems/sendDeleteItems'
    }),
    onDayClick(date) {
      if (!isFuture(date)) {
        this.dates.push(date)
      }
    },
    updateDates(dates) {
      if (!isFuture(dates[0]) || !isFuture(dates[1])) {
        this.dates = dates
      }
    },
    preSubmitCheck() {
      this.showing = true
    },
    onSubmit() {
      this.submitted = true
      this.sendDeleteItems(this.payloadParams)
    },
    clearDates() {
      this.dates = []
      this.formattedDates = []
      this.isoDates = []
      this.selectedDateItem = ''
    },
    onDateMenuItemClick(item) {
      this.selectedDateItem = item
    },
    onZorMenuItemClick(item) {
      this.selectedZorItem = item
      this.selectedZorId = item.id
    }
  },
  watch: {
    accessibleFranchisors(franchisors, oldFranchisors) {
      if (oldFranchisors.length === 0 && franchisors.length > 0) {
        this.franchisorId = franchisors[0].id
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.delete .base-dropdown-date > .base-dropdown > button,
.delete .base-dropdown-franchisors > .base-dropdown > button,
.delete .base-dropdown button button,
.delete .base-dropdown {
  justify-content: unset;
  align-items: unset;
  display: block;
  width: 100%;
}
</style>
